import { useEffect, useRef, useState, useCallback } from 'react';
import { initHashNavigation } from '../../utils/hashNavigation';

// Debounce function to prevent excessive calls
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function SmoothScroll({ children, onReady }) {
  const smoother = useRef(null);
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const initializationAttempts = useRef(0);
  const MAX_ATTEMPTS = 10;
  const mutationTimeoutRef = useRef(null);
  const hashNavigationCleanup = useRef(null);
  const readyCallbackFired = useRef(false);
  const userHasScrolled = useRef(false);
  const initialScrollY = useRef(0);
  const windowLoaded = useRef(false);
  const userInteractionCleanup = useRef(null);

  // Detect mobile devices with debouncing to prevent rapid state changes
  useEffect(() => {
    let resizeTimeout;

    const checkDevice = () => {
      const mobile = window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);

      // Mobile devices don't need ScrollSmoother, so mark as ready immediately
      if (mobile && !readyCallbackFired.current) {
        setIsReady(true);
        if (onReady) {
          onReady();
          readyCallbackFired.current = true;
        }
      }
    };

    const debouncedCheckDevice = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkDevice, 300); // Debounce by 300ms
    };

    checkDevice(); // Initial check without debounce
    window.addEventListener('resize', debouncedCheckDevice);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedCheckDevice);
    };
  }, [onReady]);

  // Check if all heavy content is loaded
  const checkContentReady = useCallback(() => {
    if (!contentRef.current) return false;
    
    // Check for heavy components
    const images = contentRef.current.querySelectorAll('img');
    const lazyElements = contentRef.current.querySelectorAll('[data-src], .lazy-load');
    
    // Check if Stats component and other heavy components are fully rendered
    const statsComponent = contentRef.current.querySelector('[data-component="stats"]');
    if (statsComponent) {
      // Check if the Stats component has its content loaded
      const hasGlobeContent = statsComponent.querySelector('canvas, .globe-container, [data-globe-loaded="true"]');
      if (!hasGlobeContent) return false;
    }
    
    // Check if all images are loaded
    for (let img of images) {
      if (!img.complete || img.naturalHeight === 0) return false;
    }
    
    // Check if lazy elements are loaded
    for (let element of lazyElements) {
      if (element.dataset.src && !element.src) return false;
    }
    
    return true;
  }, []);

  const initScrollSmoother = useCallback(() => {
    if (initializationAttempts.current >= MAX_ATTEMPTS) {
      // If we've exceeded max attempts, just mark as ready to prevent infinite loading
      setIsReady(true);
      if (onReady && !readyCallbackFired.current) {
        onReady();
        readyCallbackFired.current = true;
      }
      return false;
    }

    if (window.gsap && window.ScrollSmoother && wrapperRef.current && contentRef.current) {
      // Only check content readiness for the first 2 attempts, then proceed anyway
      if (!checkContentReady() && initializationAttempts.current < 2) {
        initializationAttempts.current++;
        return false;
      }

      try {
        if (smoother.current) {
          smoother.current.kill();
          smoother.current = null;
        }

        window.gsap.registerPlugin(window.ScrollSmoother, window.ScrollTrigger);

        // Disable ALL auto-refresh events completely
        window.ScrollTrigger.config({
          autoRefreshEvents: "none" // Disable ALL automatic refreshes
        });

        // Save initial scroll position
        initialScrollY.current = window.scrollY;

        // Override ScrollTrigger.refresh with smart throttling
        const originalRefresh = window.ScrollTrigger.refresh;
        let lastRefreshTime = Date.now();
        const REFRESH_THROTTLE = 300; // Allow refresh at most once every 300ms

        window.ScrollTrigger.refresh = function(...args) {
          const now = Date.now();
          const timeSinceLastRefresh = now - lastRefreshTime;

          // Throttle refreshes to prevent excessive calls
          if (timeSinceLastRefresh < REFRESH_THROTTLE) {
            return;
          }

          lastRefreshTime = now;
          return originalRefresh.apply(this, args);
        };

        smoother.current = window.ScrollSmoother.create({
          wrapper: wrapperRef.current,
          content: contentRef.current,
          smooth: 1.2,
          effects: false,
          smoothTouch: false,
          normalizeScroll: true,
          ignoreMobileResize: true,
          // Add debounced refresh callback to prevent ResizeObserver loops
          onRefresh: debounce(() => {
            try {
              if (window.ScrollTrigger) {
                window.ScrollTrigger.refresh();
              }
            } catch (error) {
              // Silent error handling
            }
          }, 100)
        });

        // Initialize hash navigation after ScrollSmoother is ready
        if (!hashNavigationCleanup.current) {
          hashNavigationCleanup.current = initHashNavigation();
        }

        // Track recent user interactions to distinguish user scrolls from programmatic ones
        let lastUserInteractionTime = 0;
        const USER_INTERACTION_TIMEOUT = 100; // ms

        const updateUserInteraction = () => {
          lastUserInteractionTime = Date.now();
        };

        // Track user interactions
        window.addEventListener('wheel', updateUserInteraction, { passive: true });
        window.addEventListener('touchmove', updateUserInteraction, { passive: true });
        window.addEventListener('keydown', updateUserInteraction, { passive: true });

        // Store cleanup function
        userInteractionCleanup.current = () => {
          window.removeEventListener('wheel', updateUserInteraction);
          window.removeEventListener('touchmove', updateUserInteraction);
          window.removeEventListener('keydown', updateUserInteraction);
        };

        // Override window.scrollTo to prevent unwanted automatic resets (but allow user scrolls)
        const originalScrollTo = window.scrollTo;
        window.scrollTo = function(x, y) {
          const targetY = typeof y === 'number' ? y : (x && typeof x === 'object' ? x.top : 0);
          const now = Date.now();
          const isLikelyUserInitiated = (now - lastUserInteractionTime) < USER_INTERACTION_TIMEOUT;

          // Only block programmatic resets to top, not user-initiated scrolls
          if (userHasScrolled.current && !isLikelyUserInitiated) {
            if (targetY < 5 && window.scrollY > 50) {
              return;
            }
          }

          return originalScrollTo.apply(this, arguments);
        };

        // Also override ScrollSmoother's scrollTop method
        const originalScrollTop = smoother.current.scrollTop;
        smoother.current.scrollTop = function(value) {
          const now = Date.now();
          const isLikelyUserInitiated = (now - lastUserInteractionTime) < USER_INTERACTION_TIMEOUT;

          if (arguments.length > 0) {
            if (userHasScrolled.current && !isLikelyUserInitiated && value < 5 && window.scrollY > 50) {
              return this;
            }
          }

          return originalScrollTop.apply(this, arguments);
        };

        // CRITICAL: Wait for window.load before marking as ready
        // This ensures all delayed refreshes happen before user can interact
        const markAsReady = () => {
          setIsReady(true);
          if (onReady && !readyCallbackFired.current) {
            // Small delay to ensure ScrollSmoother is fully settled
            setTimeout(() => {
              onReady();
              readyCallbackFired.current = true;
            }, 50);
          }

          // Monitor for user scroll interactions IMMEDIATELY
          const handleUserScroll = () => {
            userHasScrolled.current = true;
          };

          window.addEventListener('wheel', handleUserScroll, { passive: true, once: true });
          window.addEventListener('touchstart', handleUserScroll, { passive: true, once: true });
          window.addEventListener('keydown', (e) => {
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Space'].includes(e.key)) {
              userHasScrolled.current = true;
            }
          }, { once: true });

          // Also detect scroll events directly
          window.addEventListener('scroll', () => {
            if (window.scrollY > 5) {
              userHasScrolled.current = true;
            }
          }, { passive: true, once: true });
        };

        // If window is already loaded, mark ready immediately
        if (windowLoaded.current || document.readyState === 'complete') {
          markAsReady();
        } else {
          // Otherwise, wait for window load
          const handleLoad = () => {
            windowLoaded.current = true;
            // Short delay after load to let any final adjustments settle
            setTimeout(markAsReady, 200);
          };
          window.addEventListener('load', handleLoad, { once: true });
        }

        return true;
      } catch (error) {
        initializationAttempts.current++;
        return false;
      }
    }
    return false;
  }, [checkContentReady, onReady]);

  useEffect(() => {
    // Initialize hash navigation for mobile devices (without ScrollSmoother)
    if (isMobile) {
      // Clean up any existing ScrollSmoother when switching to mobile
      if (smoother.current) {
        try {
          smoother.current.kill();
          smoother.current = null;
        } catch (error) {
          // Silent error handling
        }
      }

      // Ensure content is visible on mobile
      if (wrapperRef.current) {
        wrapperRef.current.style.visibility = 'visible';
      }
      if (contentRef.current) {
        contentRef.current.style.visibility = 'visible';
      }

      if (!hashNavigationCleanup.current) {
        hashNavigationCleanup.current = initHashNavigation();
      }
      return;
    }

    let retryTimer;
    let observer;
    let rafId;

    const initWithRetry = () => {
      if (!initScrollSmoother()) {
        // Retry with faster initial delays for quicker initialization
        if (initializationAttempts.current < MAX_ATTEMPTS) {
          // Start with very short delays, then increase gradually
          const delays = [0, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 5000];
          const delay = delays[initializationAttempts.current] || 5000;
          retryTimer = setTimeout(initWithRetry, delay);
        }
      }
    };

    // Enhanced MutationObserver with debouncing to prevent ResizeObserver loops
    const debouncedMutationHandler = debounce((mutations) => {
      let shouldRetry = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if heavy components were added
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              if (node.matches && (
                node.matches('[data-component="stats"]') || 
                node.matches('[data-heavy-component="true"]') ||
                (node.querySelector && (
                  node.querySelector('[data-component="stats"]') ||
                  node.querySelector('[data-heavy-component="true"]')
                ))
              )) {
                shouldRetry = true;
              }
            }
          });
        }
      });

      if (shouldRetry && !smoother.current) {
        // Clear any existing timeout
        if (mutationTimeoutRef.current) {
          clearTimeout(mutationTimeoutRef.current);
        }
        
        // Use RAF to avoid blocking the main thread
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          mutationTimeoutRef.current = setTimeout(initWithRetry, 300);
        });
      }
    }, 150);

    observer = new MutationObserver(debouncedMutationHandler);

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, { 
        childList: true, 
        subtree: true,
        attributes: true,
        attributeFilter: ['data-loaded', 'data-component']
      });
    }

    // Initial attempt immediately - no delay needed as we have the loading overlay
    initWithRetry();

    // Track when window load event fires
    const handleWindowLoadEvent = () => {
      windowLoaded.current = true;

      if (!smoother.current) {
        // If ScrollSmoother hasn't initialized yet, try again
        setTimeout(initWithRetry, 200);
      } else {
        // Refresh to catch any content that loaded
        setTimeout(() => {
          try {
            if (smoother.current && window.ScrollTrigger) {
              const currentScroll = window.scrollY;
              window.ScrollTrigger.refresh();
              smoother.current.refresh();

              // Restore scroll position if needed
              if (currentScroll > 5) {
                requestAnimationFrame(() => {
                  window.scrollTo(0, currentScroll);
                  if (smoother.current) {
                    smoother.current.scrollTop(currentScroll);
                  }
                });
              }
            }
          } catch (error) {
            // Silent error handling
          }
        }, 100);
      }
    };

    window.addEventListener('load', handleWindowLoadEvent);

    return () => {
      window.removeEventListener('load', handleWindowLoadEvent);
      if (observer) observer.disconnect();
      if (retryTimer) clearTimeout(retryTimer);
      if (rafId) cancelAnimationFrame(rafId);
      if (mutationTimeoutRef.current) clearTimeout(mutationTimeoutRef.current);

      // Clean up user interaction event listeners
      if (userInteractionCleanup.current) {
        userInteractionCleanup.current();
        userInteractionCleanup.current = null;
      }

      // Clean up hash navigation
      if (hashNavigationCleanup.current) {
        hashNavigationCleanup.current();
        hashNavigationCleanup.current = null;
      }

      if (smoother.current) {
        try {
          smoother.current.kill();
          smoother.current = null;
        } catch (error) {
          // Silent error handling during cleanup
        }
      }

      initializationAttempts.current = 0;
    };
  }, [isMobile, initScrollSmoother]);

  // Prevent scrolling until ready (desktop only)
  useEffect(() => {
    if (isMobile) {
      // Ensure overflow is not hidden on mobile
      document.body.style.overflow = '';
      return;
    }

    if (!isReady) {
      // Prevent scrolling while initializing
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.documentElement.style.scrollBehavior;

      document.body.style.overflow = 'hidden';
      document.documentElement.style.scrollBehavior = 'auto';

      return () => {
        document.body.style.overflow = originalOverflow;
        document.documentElement.style.scrollBehavior = originalPosition;
      };
    } else {
      // Restore overflow when ready
      document.body.style.overflow = '';
    }
  }, [isReady, isMobile]);

  // Reset scroll position ONLY on initial mount, before initialization
  useEffect(() => {
    if (!isMobile && !isReady) {
      // Ensure we start at the top unless there's a hash
      if (!window.location.hash) {
        window.scrollTo(0, 0);
      }
    }
  }, [isMobile, isReady]);

  // Cleanup hash navigation when component unmounts
  useEffect(() => {
    return () => {
      if (hashNavigationCleanup.current) {
        hashNavigationCleanup.current();
        hashNavigationCleanup.current = null;
      }
      readyCallbackFired.current = false;
    };
  }, []);

  // Keep the same JSX structure for both mobile and desktop to prevent unmounting
  return (
    <>
      <style>{`
        .mobile-scroll {
          visibility: visible !important;
          opacity: 1 !important;
          overflow: visible !important;
          display: block !important;
        }
      `}</style>

      {/* Loading overlay - prevents interaction until ready (desktop only) */}
      {!isMobile && !isReady && (
        <div
          className="fixed inset-0 z-[9999] bg-[#0b0d0e] flex items-center justify-center"
          style={{ pointerEvents: 'all' }}
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#027DFD]"></div>
        </div>
      )}

      <div
        id="smooth-wrapper"
        ref={wrapperRef}
        className={isMobile ? "mobile-scroll" : ""}
        style={{
          visibility: isMobile ? 'visible' : (isReady ? 'visible' : 'hidden'),
          opacity: isMobile ? 1 : undefined,
          overflow: isMobile ? 'visible' : undefined
        }}
      >
        <div id="smooth-content" ref={contentRef} style={isMobile ? { visibility: 'visible', display: 'block' } : undefined}>
          {children}
        </div>
      </div>
    </>
  );
}